<template>
  <div>
    <div v-if="!printing" style="margin: auto;" class="pt-3 fit-content">
      <date-picker :selected-date="selectedDate" @onDateUpdate="d => selectedDate = d" />
    </div>
    <v-flex
      xs12
      lg10
      xl8
      style="margin: auto;"
      class="pb-5 px-2 orders-list-table"
    >
      <v-card>
        <v-card-title primary-title :style="{ display: $vuetify.breakpoint.xs ? 'block' : ''}" class="pb-0">
          <h3 class="headline">{{ $t('ordersListContainer.ordersFor', { day: currentDay }) }}</h3>
          <v-spacer />
          <v-text-field
            v-if="!printing"
            v-model="searchQuery"
            append-icon="search"
            :label="$t('common.search')"
            single-line
            hide-details
          />
        </v-card-title>
        <v-card-title v-if="!printing && isAdmin" class="py-0 pl-2">
          <dialog-wrapper :visible="dialog">
            <v-dialog v-model="dialog" width="500">
              <v-btn slot="activator" color="primary"><span v-t="'ordersListContainer.viewUsersWithoutRequests'" /></v-btn>
              <users-without-requests-modal v-if="dialog" :date="selectedDate" />
            </v-dialog>
          </dialog-wrapper>
        </v-card-title>
        <v-data-table
          :rows-per-page-text="$t('dataTables.rowsPerPage')"
          :no-data-text="$t('dataTables.noData')"
          :loading="loading > 0"
          :hide-headers="printing"
          :headers="headers"
          :items="filteredRequestsForDay"
          :hide-actions="printing"
          class="elevation-1"
          :rows-per-page-items="rowsPerPage"
        >
          <template slot="items" slot-scope="props">
            <td style="min-width: 190px;">
              {{ props.item.name }}
              <div v-if="props.item.comment" class="grey--text textwrap">
                {{ props.item.comment }}
              </div>
            </td>
            <td class="text-xs-left">
              <span v-for="(soup, i) in props.item.soups" :key="i">
                <span :class="`${soup.color}--text`">{{ soup.title }}</span><span v-if="i !== props.item.soups.length - 1">, </span>
              </span>
            </td>
            <td class="text-xs-left">
              <span v-for="(main, i) in props.item.mains" :key="i">
                <span :class="`${main.color}--text`">{{ main.title }}</span><span v-if="i !== props.item.mains.length - 1">, </span>
              </span>
            </td>
          </template>
          <template v-if="(soupsCount > 0 || mainDishesCount > 0) && isAdmin" slot="footer">
            <td colspan="100%" class="pr-4">
              <div><strong><span v-t="'dataTables.soupsCount'" /> {{ soupsCount }}</strong></div>
              <div><strong><span v-t="'dataTables.mainsCount'" /> {{ mainDishesCount }}</strong></div>
            </td>
          </template>
          <template slot="pageText" slot-scope="props">
            {{ $t('dataTables.pagingRow', { start: props.pageStart, stop: props.pageStop, length: props.itemsLength }) }}
          </template>
        </v-data-table>
      </v-card>
    </v-flex>
    <v-btn
      v-if="!printing"
      fab
      fixed
      right
      bottom
      color="primary"
      @click="print"
    >
      <v-icon>print</v-icon>
    </v-btn>
    <div v-show="printing && uniqueDishNames.length > 0">
      <div class="pagebreak" />
      <table>
        <v-layout wrap>
          <div v-for="dish in uniqueDishNames" :key="dish" class="printable-dish-name">
            {{ dish }}
          </div>
        </v-layout>
      </table>
    </div>
  </div>
</template>

<script>
import moment from 'moment';
import gql from 'graphql-tag';
import { mapGetters } from 'vuex';
import NotificationService from '@/services/NotificationService';
import ErrorHandler from '@/services/ErrorHandler';
import flatten from 'lodash/flatten';
import AuthenticationService from '@/services/AuthenticationService';
import uniq from 'lodash/uniq';
import flatmap from 'lodash/flatMap';
import DatePicker from '../common/date-picker/DatePicker.vue';
import UsersWithoutRequestsModal from './UsersWithoutRequestsModal.vue';
import DialogWrapper from '../common/dialog-wrapper/DialogWrapper.vue';

export default {
  name: 'OrdersListContainer',
  components: {
    'date-picker': DatePicker,
    'users-without-requests-modal': UsersWithoutRequestsModal,
    DialogWrapper,
  },
  data() {
    return {
      searchQuery: '',
      loading: 0,
      day: null,
      printing: false,
      dialog: false,
      selectedDate: this.$route.params.date,
    };
  },
  computed: {
    ...mapGetters('userInfoStore', ['userData']),
    currentDay() {
      const day = moment(this.selectedDate).get('day');
      const translatedDay = this.$t(`common.forDay-${day}`);

      return translatedDay;
    },
    rowsPerPage() {
      return [10, 20, 50, { text: this.$t('dataTables.all'), value: -1 }];
    },
    isFree() {
      return this.day ? this.day.isFreeOfCharge : false;
    },
    headers() {
      const defaultHeaders = [
        {
          text: this.$t('ordersListContainer.name'), align: 'left', sortable: true, value: 'name',
        },
        {
          text: this.$t('ordersListContainer.soup'), value: 'soup', align: 'left', sortable: false,
        },
        {
          text: this.$t('ordersListContainer.mainDish'), value: 'main', align: 'left', sortable: false,
        },
      ];

      return defaultHeaders;
    },
    requestsForDay() {
      return !this.day ? [] : this.day.requests.map(r => ({
        name: r.user.name,
        soups: r.dishes.filter(d => d.isSoup),
        mains: r.dishes.filter(d => !d.isSoup),
        price: r.price,
        comment: r.comment,
      }));
    },
    filteredRequestsForDay() {
      const query = this.searchQuery.toLowerCase();

      return this.requestsForDay.filter(r => r.name.toLowerCase().indexOf(query) !== -1
        || r.soups.some(s => s.title.toLowerCase().indexOf(query) !== -1)
        || r.mains.some(s => s.title.toLowerCase().indexOf(query) !== -1));
    },
    uniqueDishNames() {
      if (!this.day || !this.day.isFreeOfCharge) {
        return [];
      }

      const dishes = uniq(flatten(this.day.requests.map(r => r.dishes.map(d => d.title))));

      return dishes;
    },
    mainDishesCount() {
      const count = !this.day ? 0 : flatmap(this.day.requests, 'dishes').filter(d => !d.isSoup).length;

      return count;
    },
    soupsCount() {
      const count = !this.day ? 0 : flatmap(this.day.requests, 'dishes').filter(d => d.isSoup).length;

      return count;
    },
    isAdmin() {
      return AuthenticationService.isAdmin();
    },
  },
  watch: {
    selectedDate(newValue) {
      this.$router.push({ params: { date: newValue } });
    },
  },
  mounted() {
    window.onbeforeprint = () => {
      this.printing = true;
    };
    window.onafterprint = () => {
      this.printing = false;
    };
  },
  beforeDestroy() {
    window.onbeforeprint = null;
    window.onafterprint = null;
  },
  methods: {
    viewUsersWithoutRequests() {

    },
    print() {
      this.printing = true;
      setTimeout(() => window.print());
    },
    getSoup(dishes) {
      const dish = dishes.filter(d => d.isSoup).map(d => d.title).join(', ');
      return dish || '-';
    },
    getMain(dishes) {
      const dish = dishes.filter(d => !d.isSoup).map(d => d.title).join(', ');
      return dish || '-';
    },
  },
  apollo: {
    $loadingKey: 'loading',
    day: {
      fetchPolicy: 'network-only',
      query: gql`query OrdersListQuery($date: TimelessDate!, $languageId: Int) {
        day(date: $date) {
          id
          isFreeOfCharge
          requests {
            comment
            user {
              name: displayName
            }
            dishes {
              color
              title(languageId: $languageId)
              isSoup
            }
          }
        }
      }`,
      variables() {
        return {
          date: this.selectedDate,
          languageId: this.userData.languageId,
        };
      },
      skip() {
        return !this.selectedDate;
      },
      error(error) {
        this.day = null;
        ErrorHandler.handleApolloError(error);
      },
      result(r) {
        if (!r.loading) {
          NotificationService.clearNetworkNotification();
        }
      },
    },
  },
};
</script>

<style scoped>
.textwrap {
  max-width: 300px;
  overflow-wrap: break-word;
}

  @media print {
    .orders-list-table {
      flex-basis: 100% !important;
      max-width: 100% !important;
      padding: 0 !important;
    }

    .orders-list-table >>> .card {
      border: 1px solid black;
    }

    .orders-list-table >>> .card__title {
      border-bottom: 1px solid black;
      padding-top: 3px;
      padding-bottom: 3px;
    }

    .orders-list-table >>> thead {
      display: none;
    }

    .orders-list-table >>> td {
      height: 18px;
      font-size: small;
    }

    .orders-list-table >>> tr:nth-child(even) {
      background-color: rgba(204, 204, 204, 0.25);
      -webkit-print-color-adjust: exact;
    }

    .printable-dish-name {
      border: black;
      border-width: 1px;
      border-style: solid;
      background-color: white;
      text-align: center;
      margin: 1px;
      padding: 4px 16px;
      font-size: 30px;
    }

    .pagebreak {
      page-break-before: always;
    }
  }
</style>
